import {
  getBlocksuiteLocaleKey,
  LocaleProvider,
  type OpenDocMode,
  type ToolbarAction,
  translateBlocksuiteTextWithTextFallback,
  ToolbarContext,
} from '@blocksuite/affine-shared/services';
import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { PropTypes, requiredProperties } from '@blocksuite/std';
import { computed, type ReadonlySignal } from '@preact/signals-core';
import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { repeat } from 'lit-html/directives/repeat.js';

import { EditorChevronDown } from '../toolbar';

@requiredProperties({
  actions: PropTypes.array,
  context: PropTypes.instanceOf(ToolbarContext),
  openDocModeSignal: PropTypes.object,
  updateOpenDocMode: PropTypes.instanceOf(Function),
})
export class OpenDocDropdownMenu extends SignalWatcher(
  WithDisposable(LitElement)
) {
  static override styles = css`
    :host {
      display: flex;
      gap: unset !important;
    }

    div[data-orientation] {
      width: 264px;
      gap: 4px;
      min-width: unset;
      overflow: unset;
    }

    editor-menu-action {
      .label {
        display: flex;
        flex: 1;
        justify-content: space-between;
      }

      .shortcut {
        color: ${unsafeCSSVarV2('text/secondary')};
      }
    }
  `;

  @property({ attribute: false })
  accessor actions!: (ToolbarAction & {
    mode: OpenDocMode;
    shortcut?: string;
  })[];

  @property({ attribute: false })
  accessor context!: ToolbarContext;

  @property({ attribute: false })
  accessor openDocModeSignal!: ReadonlySignal<OpenDocMode>;

  @property({ attribute: false })
  accessor updateOpenDocMode!: (mode: OpenDocMode) => void;

  private _translate(
    context: ToolbarContext,
    kind: 'label' | 'tooltip',
    value: string
  ) {
    return translateBlocksuiteTextWithTextFallback(
      context.std.getOptional(LocaleProvider),
      getBlocksuiteLocaleKey(`toolbar.${kind}`, value),
      value,
      `toolbar.${kind}`
    );
  }

  currentAction$ = computed(() => {
    const currentOpenDocMode = this.openDocModeSignal.value;
    return (
      this.actions.find(a => a.mode === currentOpenDocMode) ?? this.actions[0]
    );
  });

  override render() {
    const {
      actions,
      context,
      updateOpenDocMode,
      currentAction$: { value: currentAction },
    } = this;
    const currentActionLabel = this._translate(
      context,
      'label',
      currentAction.label ?? currentAction.id
    );
    const openLabel = this._translate(context, 'label', 'Open');

    return html`
      <editor-icon-button
        aria-label=${currentActionLabel}
        .tooltip=${this._translate(
          context,
          'tooltip',
          currentAction.label ?? currentAction.id
        )}
        @click=${() => currentAction.run?.(context)}
      >
        ${currentAction.icon}
        <span class="label">${openLabel}</span>
      </editor-icon-button>
      <editor-menu-button
        aria-label=${this._translate(context, 'label', 'Open doc menu')}
        .contentPadding="${'8px'}"
        .button=${html`
          <editor-icon-button
            aria-label=${this._translate(context, 'label', 'Open doc with')}
          >
            ${EditorChevronDown}
          </editor-icon-button>
        `}
      >
        <div data-orientation="vertical">
          ${repeat(
            actions,
            action => action.id,
            ({ id, label, icon, run, disabled, mode, shortcut }) => {
              const translatedLabel = this._translate(
                context,
                'label',
                label ?? id
              );
              return html`
                <editor-menu-action
                  aria-label=${ifDefined(translatedLabel)}
                  ?disabled=${ifDefined(disabled)}
                  @click=${() => {
                    run?.(context);
                    updateOpenDocMode(mode);
                  }}
                >
                  ${icon}
                  <div class="label">
                    ${translatedLabel}
                    <span class="shortcut">${shortcut}</span>
                  </div>
                </editor-menu-action>
              `;
            }
          )}
        </div>
      </editor-menu-button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-open-doc-dropdown-menu': OpenDocDropdownMenu;
  }
}
